import * as XLSX from 'xlsx';
import { extractErrorMessage } from "@common/utils/errorUtils";

interface TableColumn {
    key: string;
    label: string;
    width?: string;
    type?: 'text' | 'date' | 'checkbox' | 'boolean' | 'currency' | 'percentage'|'html';
    currency?: string;
    format?: string;
    /** When true, this boolean column is treated as the "active" column and gets a header filter (active/inactive/all). */
    activeFilterColumn?: boolean;
}

/**
 * Reusable Table Component with Pagination Support
 *
 * Usage example:
 * <reusable-table
 *   columns="$ctrl.tableColumns"
 *   data="$ctrl.tableData"
 *   enable-pagination="true"
 *   title="My Table"
 *   on-page-change="$ctrl.onPageChanged()">
 * </reusable-table>
 *
 * Pagination features:
 * - Default: 5 items per page (configurable: 5, 10, 25, 50)
 * - Pagination automatically enabled only when data exceeds items per page
 * - Can be manually disabled by setting enable-pagination="false"
 * - on-page-change callback for external handling if needed
 */


class ReusableTableController {
    public columns: TableColumn[];
    public displayColumns: boolean = true;
    public data: any[];
    public emptyMessage: string = 'No items found.';
    public loading: boolean = false;
    public title: string;
    public addButtonText: string = 'Add';
    public addButtonIcon: string = 'add';
    public readonly: boolean = false;
    public itemIdField: string = 'id';
    public selectedItems: Set<any> = new Set();
    public hideExcelButton: boolean = false;
    public hideDeleteButton: boolean = false;
    public hideActionsButton: boolean = false;
    public hideHeader: boolean = false;
    public autoSizeColumns: boolean = false;

    // Expandable row properties
    public expandedRows: Set<any> = new Set();
    public showExpandIcon: boolean = false; // Show expand icon column when expandable
    public alternateExpandedRowFontColors: boolean = false; // Alternate blue/green font colors for expanded rows

    // Pagination properties
    public paginationOptions: any = {
        currentPage: 1,
        itemsPerPage: 5,
        totalItems: 0
    };
    public paginatedData: any[] = [];
    public enablePagination: boolean = false;

    /** FE-only filter for the active column (key 'active' and type boolean). Default 'active'. */
    public activeColumnFilter: 'active' | 'inactive' | 'all' = 'active';

    public onAdd: () => void;
    public onEdit: (params: { item: any }) => void;
    public onRetry: (params: { item: any }) => void;
    public onDelete: (params: { itemIds: any[] }) => void | angular.IPromise<void>;
    public onPageChange: () => void;
    public onRefresh: () => void;
    public onRowClick: (params: { item: any }) => void;
    public customCellTemplate: (params: { item: any, column: TableColumn }) => string | null;
    public onSave: (params: { item: any }) => void;
    public onCancel: (params: { item: any }) => void;
    public isRowDisabled: (params: { item: any }) => boolean;
    public getDisabledRowTooltip: (params: { item: any }) => string | null | undefined;
    public disabledRowClass: string = 'disabled-row';

    private notifyjs: any;
    private $sce: angular.ISCEService;

    /** @ngInject */
    constructor(notifyjs: any, $sce: angular.ISCEService) {
        this.notifyjs = notifyjs;
        this.$sce = $sce;
    }

    public $onInit(): void {
        this.updatePagination();
    }

    public $onChanges(changes: any): void {
        // When data changes, remove any disabled items from selection
        if (changes.data && this.data && this.selectedItems) {
            const itemsToRemove: any[] = [];
            this.selectedItems.forEach((item: any) => {
                if (this.isRowDisabled && this.isRowDisabled({ item: item })) {
                    itemsToRemove.push(item);
                }
            });
            itemsToRemove.forEach(item => this.selectedItems.delete(item));
        }
        if (changes.data && changes.data.currentValue) {
            this.updatePagination();
        }
    }

    /** Returns the column used for active/inactive filtering (key 'active' or 'isActive' and type boolean, or activeFilterColumn). */
  public getActiveColumn(): TableColumn | undefined {
      if (!this.columns) return undefined;
      return this.columns.find(c =>
          (c.type === 'boolean' && (c.key === 'active' || c.key === 'isActive')) || !!c.activeFilterColumn
      );
  }

  /** Data after applying the active-column filter only (FE filter, not sent to BE). */
  public getFilteredData(): any[] {
      if (!this.data) return [];
      const activeCol = this.getActiveColumn();
      if (!activeCol) return this.data;
      const key = activeCol.key;
      if (this.activeColumnFilter === 'active') {
          return this.data.filter((item: any) => !!item[key]);
      }
      if (this.activeColumnFilter === 'inactive') {
          return this.data.filter((item: any) => !item[key]);
      }
      return this.data;
  }

  public onActiveColumnFilterChange(): void {
      this.updatePagination();
  }

  public getColumnClass(column: TableColumn): string {
      if (column.width) {
          return column.width;
      }
      return '';
  }

  public getColumnStyle(column: TableColumn): string {
      // If auto-sizing is enabled, don't set explicit widths
      if (this.autoSizeColumns) {
          return '';
      }

      if (column.width) {
          // If width is a CSS value (like '200px', '15%'), use it directly
          if (column.width.includes('px') || column.width.includes('%') || column.width.includes('em')) {
              return `width: ${column.width};`;
          }
          // If it's a Bootstrap class, we'll ignore it for table cells
          return '';
      }
      // Calculate equal width for columns without explicit width
      const totalColumns = this.columns ? this.columns.length : 1;
      const columnsWithoutWidth = this.columns ? this.columns.filter(c => !c.width).length : 1;
      if (columnsWithoutWidth > 0) {
          const equalWidthPercent = (100 / totalColumns);
          return `width: ${equalWidthPercent}%;`;
      }
      return '';
  }

    public getExpandedRowColumnClass(column: TableColumn, index: number): string {
    const baseClass = this.getColumnClass(column);
    if (index === 0) {
      return baseClass ? `${baseClass} expanded-row-first-column` : 'expanded-row-first-column';
    }
    return baseClass;
  }

    /** DD/MM/YYYY for date columns (not browser default, which is often MM/DD/YYYY). */
    private formatDateCell(value: any): string {
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            return value != null ? String(value) : '---';
        }
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    public formatValue(item: any, column: TableColumn): string {
        const value = item[column.key];

        if (value === null || value === undefined || value === '' || value === ' ') {
            return '---';
        }

      if (column.type === 'date' && value) {
          return this.formatDateCell(value);
      }

      if (column.type === 'boolean') {
          return value ? 'true' : 'false';
      }

      if (column.type === 'currency' || column.format === 'currency') {
          const numeric = typeof value === 'string'
              ? parseFloat(value.replace(/[^0-9.-]/g, ''))
              : Number(value);
          if (isNaN(numeric)) {
              return value.toString();
          }
          const formattedValue = numeric.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          });
          return column.currency ? `${column.currency} ${formattedValue}` : formattedValue;
      }

      if (column.type === 'percentage') {
          const formattedValue = Number(value).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          });

          // Remove negative sign if value is -0.00
          const cleanedValue = formattedValue === '-0.00' ? '0.00' : formattedValue;
          return `${cleanedValue} %`;
      }

      return value.toString();
  }

    public formatValueAsHtml(item: any, column: TableColumn): any {
        const value = this.formatValue(item, column);
        if (column.type === 'html' || (typeof value === 'string' && value.includes('<br>'))) {
            return this.$sce.trustAsHtml(value);
        }
        return value;
    }

    public shouldRenderAsHtml(column: TableColumn, value: any): boolean {
        return column.type === 'html' || (typeof value === 'string' && value.includes('<br>'));
    }

    private stripHtml(htmlString: string): string {
        // If the value contains HTML, strip it out
        if (htmlString.includes('<br>') || htmlString.includes('<span') || htmlString.includes('<')) {
            // Create a temporary DOM element to extract text content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlString;
            const plainText = tempDiv.textContent || tempDiv.innerText || '';
            // Clean up any extra whitespace and normalize line breaks
            return plainText.replace(/\s+/g, ' ').trim();
        }

        return htmlString;
    }

    public getPlainTextValue(item: any, column: TableColumn): string {
        const value = item[column.key];

        if (value === null || value === undefined || value === '' || value === ' ') {
            return '';
        }

        // Get the formatted value (which may contain HTML)
        const formattedValue = this.formatValue(item, column);

        // Skip tooltip for empty formatted values
        if (formattedValue === '---' || !formattedValue) {
            return '';
        }

        // Strip HTML from the formatted value
        const stringValue = typeof formattedValue === 'string' ? formattedValue : String(formattedValue);
        return this.stripHtml(stringValue);
    }

    public hasData(): boolean {
        return this.paginatedData && this.paginatedData.length > 0;
    }

  public getRowCount(): number {
      return this.getFilteredData().length;
  }

  public updatePagination(): void {
      const filteredData = this.getFilteredData();
      if (!filteredData.length && !(this.data && this.data.length)) {
          this.paginatedData = [];
          this.paginationOptions.totalItems = 0;
          this.enablePagination = false;
          return;
      }

      this.paginationOptions.totalItems = filteredData.length;

      // Clamp current page when filtered set shrinks (e.g. switching active filter)
      const maxPage = Math.max(1, Math.ceil(filteredData.length / this.paginationOptions.itemsPerPage));
      if (this.paginationOptions.currentPage > maxPage) {
          this.paginationOptions.currentPage = maxPage;
      }

      // Enable pagination only if data exceeds max items per page
      this.enablePagination = filteredData.length > this.paginationOptions.itemsPerPage;

      if (this.enablePagination) {
          const startIndex = (this.paginationOptions.currentPage - 1) * this.paginationOptions.itemsPerPage;
          const endIndex = startIndex + this.paginationOptions.itemsPerPage;
          this.paginatedData = filteredData.slice(startIndex, endIndex);
      } else {
          this.paginatedData = filteredData;
      }
  }

  public onPageChanged(): void {
      this.updatePagination();
      if (this.onPageChange) {
          this.onPageChange();
      }
  }

  public onItemsPerPageChanged(): void {
      this.paginationOptions.currentPage = 1;
      this.updatePagination();
      if (this.onPageChange) {
          this.onPageChange();
      }
  }

    public toggleSelection(item: any): void {
      if (this.isRowDisabled && this.isRowDisabled({ item: item })) {
          return; // Don't allow selection of disabled rows
      }
      if (this.selectedItems.has(item)) {
          this.selectedItems.delete(item);
      } else {
          this.selectedItems.add(item);
      }
  }

  public isSelected(item: any): boolean {
      return this.selectedItems.has(item);
  }

  public getSelectedCount(): number {
      return this.selectedItems.size;
  }

  public hasSelectedItems(): boolean {
      return this.selectedItems.size > 0;
  }

  public selectAll(): void {
      if (this.paginatedData) {
          // Add all items from current page to selection (dont clear existing selections)
          // Skip disabled rows
          this.paginatedData.forEach(item => {
              if (!this.isRowDisabled || !this.isRowDisabled({ item: item })) {
                  this.selectedItems.add(item);
              }
          });
      }
  }

  public deselectAll(): void {
      // Only deselect items from the current page, not all selections
      if (this.paginatedData) {
          this.paginatedData.forEach(item => this.selectedItems.delete(item));
      }
  }

  public isAllSelected(): boolean {
      // Check if ALL items on the CURRENT page are selected
      if (!this.paginatedData || this.paginatedData.length === 0) {
          return false;
      }
      // All non-disabled items on current page must be in the selection
      const selectableItems = this.paginatedData.filter(item => 
          !this.isRowDisabled || !this.isRowDisabled({ item: item })
      );
      if (selectableItems.length === 0) {
          return false;
      }
      return selectableItems.every(item => this.selectedItems.has(item));
  }

  public toggleSelectAll(): void {
      if (this.isAllSelected()) {
          // If all current page items are selected, deselect only current page items
          this.deselectAll();
      } else {
          // If not all current page items are selected, select all current page items
          this.selectAll();
      }
  }

  public selectAllFiltered(): void {
      // Select all items in the filtered dataset (FE active filter applied)
      // Skip disabled rows
      const filtered = this.getFilteredData();
      if (filtered.length) {
          filtered.forEach(item => {
              if (!this.isRowDisabled || !this.isRowDisabled({ item: item })) {
                  this.selectedItems.add(item);
              }
          });
      }
  }

  public deselectAllFiltered(): void {
      // Deselect all items in the filtered dataset (FE active filter applied)
      const filtered = this.getFilteredData();
      if (filtered.length) {
          filtered.forEach(item => this.selectedItems.delete(item));
      }
  }

  public editItem(item: any): void {
      if (this.readonly) return;
      if (this.onEdit) {
          this.onEdit({item: item});
      }
  }

  public retryItem(item: any): void {
      if (this.onRetry) {
          this.onRetry({item: item});
      }
  }

  public confirmDelete(event?: MouseEvent): void {
      if (event) {
          event.stopPropagation();
          event.preventDefault();
      }

      if (this.hasSelectedItems()) {
          const selectedIds = Array.from(this.selectedItems).map(item => item[this.itemIdField]);
          this.deleteSelectedItems(selectedIds);
      }
  }

  private deleteSelectedItems(itemIds): void {
      const selectedCount = itemIds.length;

      if (!this.onDelete) {
          return;
      }

      const result: void | angular.IPromise<void> = this.onDelete({ itemIds: itemIds });

      // If onDelete returns a Promise (e.g. parent shows confirm modal then calls API),
      // await it: on success show success and clear selection; on failure show the returned error message.
      if (result != null && typeof (result as any).then === 'function') {
          (result as angular.IPromise<void>).then(() => {
              this.selectedItems.clear();
              this.notifyjs(`${selectedCount} item(s) deleted successfully`, "success");
          }).catch((err) => {
              this.notifyjs(extractErrorMessage(err, 'Delete failed.'), "error");
              // Leave selection as-is so user can retry
          });
          return;
      }

      // Legacy: synchronous delete – update local data and clear selection
      this.data = this.data.filter(item => !this.selectedItems.has(item));
      this.selectedItems.clear();
  }

  public refreshData(): void {
      if (this.onRefresh) {
          this.onRefresh();
      }
  }

  public rowClick(item: any): void {
      if (this.onRowClick) {
          this.onRowClick({ item: item });
      }
  }

  public downloadExcel(): void {
      if (!this.columns || this.columns.length === 0) {
          this.notifyjs('No columns defined for export', 'warning');
          return;
      }

      const dataToExport = this.getFilteredData();
      if (!dataToExport || dataToExport.length === 0) {
          this.notifyjs('No data available to export', 'warning');
          return;
      }

      try {
          // Create workbook and worksheet
          const workbook = XLSX.utils.book_new();

          // Prepare headers from columns
          const headers = this.columns.map(column => column.label);

          // Prepare data rows (export filtered data so Excel matches what user sees)
          const dataRows = dataToExport.map(item => {
              return this.columns.map(column => {
                  // Use formatValue to get consistent formatting, then strip HTML
                  const formattedValue = this.formatValue(item, column);
                  
                  // Convert '---' placeholder to empty string for Excel export
                  if (formattedValue === '---') {
                      return '';
                  }

                  // Strip HTML from the formatted value (same as cell hover title)
                  return this.stripHtml(formattedValue);
              });
          });

          // Combine headers and data
          const worksheetData = [headers, ...dataRows];

          // Create worksheet
          const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

          // Set column widths
          const columnWidths = this.columns.map(column => {
              let width = 15; // default width
              if (column.width) {
                  // Handle different width formats: 'col-md-2', '2', etc.
                  const widthMatch = column.width.toString().match(/\d+/);
                  if (widthMatch) {
                      width = parseInt(widthMatch[0]) * 2;
                  }
              }
              return { wch: Math.max(width, 10) }; // minimum width of 10
          });
          worksheet['!cols'] = columnWidths;

          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

          // Generate filename with current date (sanitize title)
          const sanitizedTitle = (this.title || 'table_data')
              .replace(/[^a-z0-9]/gi, '_')
              .toLowerCase();
          const fileName = sanitizedTitle + '_' +
                          new Date().toISOString().split('T')[0] + '.xlsx';

          // Write file
          XLSX.writeFile(workbook, fileName);

          this.notifyjs('Excel file downloaded successfully', 'success');
      } catch (error) {
          console.error('Error exporting to Excel:', error);
          this.notifyjs('Error exporting to Excel. Please ensure xlsx library is installed.', 'error');
      }
  }

  public isRowExpanded(item: any): boolean {
      return this.expandedRows.has(item);
  }

  public toggleRowExpansion(item: any, event?: MouseEvent): void {
      if (event) {
          event.stopPropagation();
      }

      if (this.expandedRows.has(item)) {
          this.expandedRows.delete(item);
      } else {
          this.expandedRows.add(item);
      }
  }

  public getExpandIcon(item: any): string {
      return this.isRowExpanded(item) ? 'arrow_drop_up' : 'arrow_drop_down';
  }

  public getSelectedItems(): any[] {
      return Array.from(this.selectedItems);
  }
}

export const ReusableTableComponent = {
    template: require('./reusable-table.component.html'),
    style: require('./reusable-table.component.scss'),
    controller: ReusableTableController,
    controllerAs: '$ctrl',
    bindings: {
        columns: '<',
        displayColumns: '<?',
        data: '<',
        emptyMessage: '@?',
        loading: '<?',
        title: '@?',
        addButtonText: '@?',
        addButtonIcon: '@?',
        readonly: '<?',
        itemIdField: '@?',
        enablePagination: '<?',
        hideExcelButton: '<?',
        showExpandIcon: '<?',
        hasExpandableRows: '<?',
        alternateExpandedRowFontColors: '<?',
        autoSizeColumns: '<?',
        hideDeleteButton: '<?',
        hideActionsButton: '<?',
        hideHeader: '<?',

        onAdd: '&?',
        onEdit: '&?',
        onRetry: '&?',
        onDelete: '&?',
        onPageChange: '&?',
        onRefresh: '&?',
        onRowClick: '&?',
        customCellTemplate: '&?',
        onSave: '&?',
        onCancel: '&?',
        isRowDisabled: '&?',
        getDisabledRowTooltip: '&?',
        disabledRowClass: '@?'
    }
};



// WEBPACK FOOTER //
// ./src/components/reusable-table/reusable-table.component.ts